import { VIEWER_ERROR_CODES, type ViewerErrorCode } from './api.js';

/**
 * 表示文言。
 *
 * **v1 は `ja` のみ実装する**([#33](../../../../docs/issues/33-viewer-embed/DESIGN.md))。
 * `data-signa-lang` は将来の拡張点として口だけ開けており、未知の値は `ja` に落ちる。
 */

export type Lang = 'ja';

export const MESSAGES = {
  ja: {
    loading: '読み込み中…',
    // **画像も受け付けるようになった**(#158)。案内も素材で言う。
    dropHint: '動画・画像をここにドロップ',
    /**
     * **盤面の中に出す案内**(#208)。**素材が入っていないセルだけ。**
     *
     * [`dropHint`](#dropHint) と役割が違う —— あちらは**ドラッグ中**に
     * 「落とせる」と示すもので、**ドラッグを始めた人にしか見えない。**
     * こちらは**何もしていない状態**で「ここに入れられる」と伝える。
     *
     * **短くする。** 枠の中に 1 行で収める必要があり、分割した盤面では
     * セルが狭い。入らないセルでは描画コアが文言を落とす(`fitsHint`)。
     */
    emptyCellDrop: 'ここに動画をドロップ',
    /**
     * 同じ場所に出す、**タッチ端末向け**の案内(#208)。
     *
     * **タッチ端末では D&D で素材を入れられない**(#159 で「素材を選ぶ」
     * ボタンを廃止した)。「ここにドロップ」をそのまま出すと**できない操作を
     * 案内する**ことになるので、**別の端末で試せる**ことを伝える。
     *
     * 出し分けは CSS(`(hover: none) and (pointer: coarse)`)—— JS で判定して
     * 保持すると、**タッチ対応のノート PC を誤判定**する。
     */
    emptyCellTouch: 'PC で試せます',
    // **素材を当てるまで出す**(#159 で「常時表示」から変更)。
    // すべて解除したら戻す —— 消えたままだと「送信されたのでは」と読める。
    // **担保は表示ではなく、送信のコードが存在しないこと**(設計書 9.5)。
    noUpload: '素材はサーバに送信されません',
    play: '再生',
    pause: '停止',
    // **「同期」は先頭合わせ**(#123 と同じ定義)。押すと全部を 0 秒から
    // 流し直し、そのあとも定期的にずれを見張る。
    sync: '同期',
    // **「同期」の失敗ではない。** 自動再生の拒否と「再生」の失敗の両方で使う。
    playFailed: '再生を開始できませんでした',
    retry: '再試行',
    notAvailable: 'このプレビューは現在ご利用いただけません',
    // **閲覧者に「登録して」とは言わない**(埋め込み先の管理者の仕事)。
    // 何を直すかはコンソールに出す(api.ts)。
    originNotAllowed: 'このサイトではプレビューを表示できません',
    network: '読み込めませんでした',
    unsupported: 'この環境では動画の合成表示に対応していません',
    videoUnsupported: 'この動画形式は再生できません',
    // **画像には「再生」と言わない。** 直しようのない案内にしない(#158)。
    imageUnsupported: 'この画像形式は表示できません',
    // **上限の数値は呼び出し側が添える**(環境によって違うため)。
    tooLarge: '素材が大きすぎて表示できません',
    /** 上限の数値を添える(環境によって違う)。`{n}` を置き換える。 */
    tooLargeLimit: '素材が大きすぎて表示できません(一辺 {n}px まで)',
    renderFailed: '表示できませんでした',
    // **動画と画像の 2 種を受け付ける**(#158)。SVG は対象外(ADR-0030)。
    notSupported: '動画または画像のファイルを選んでください',
    chooseFrame: '動画を入れる枠を選んでください',
    /**
     * 共有していた素材が待っている間に外された(#168)。
     *
     * **「失敗」とは言わない。** 利用者の操作が速すぎただけで、
     * もう一度入れれば通る。
     */
    retryAssign: '素材の割り当てが変わりました。もう一度入れてください',
  },
} as const satisfies Record<Lang, Record<string, string>>;

/** 未知の言語指定は `ja` に落とす。 */
export function resolveLang(raw: string | null | undefined): Lang {
  return raw === 'ja' ? 'ja' : 'ja';
}

export function messages(lang: Lang = 'ja'): (typeof MESSAGES)['ja'] {
  return MESSAGES[lang];
}

/**
 * エラーコードに対応する表示文言。
 *
 * **`INVALID_TOKEN` は何も表示しない**(埋め込みミスであり、閲覧者に
 * 見せる意味がない — #33)。`BACKGROUND_PENDING` はローディング継続。
 */
export function errorMessage(code: ViewerErrorCode, lang: Lang = 'ja'): string | null {
  const m = messages(lang);
  switch (code) {
    case VIEWER_ERROR_CODES.NOT_AVAILABLE:
      return m.notAvailable;
    case VIEWER_ERROR_CODES.NETWORK:
      return m.network;
    case VIEWER_ERROR_CODES.ORIGIN_NOT_ALLOWED:
      return m.originNotAllowed;
    case VIEWER_ERROR_CODES.UNSUPPORTED:
      return m.unsupported;
    case VIEWER_ERROR_CODES.VIDEO_UNSUPPORTED:
      return m.videoUnsupported;
    case VIEWER_ERROR_CODES.RENDER_FAILED:
      return m.renderFailed;
    case VIEWER_ERROR_CODES.INVALID_TOKEN:
    case VIEWER_ERROR_CODES.BACKGROUND_PENDING:
      return null;
  }
}

/** そのエラーで手動の再試行を出すか。 */
export function isRetryable(code: ViewerErrorCode): boolean {
  return code === VIEWER_ERROR_CODES.NETWORK;
}
